import { useEffect, useRef, useState } from 'react'
import { useAsistenciaApi } from '../api'
import { t as tBase, mesLabel, Lang } from '../i18n'
import { t, estadoLabel, ESTADOS_F11, EstadoF11 } from './i18n'
import { EstadoChip } from './EstadoChip'
import styles from '../FichaOverlay.module.css'
import dialogStyles from '../../styles/Dialog.module.css'
import { formatDate, localeFor } from '../../utils/format'
import { RolSelector, Grupo } from '../roles'
import { guardarFichero } from '../descargar'

function progressClass(pct: number): string {
  if (pct >= 85) return styles.progressSuccess
  if (pct >= 60) return styles.progressWarning
  return styles.progressDanger
}

// Mismos umbrales que progressClass (F11: 85/60), pero para colorear el texto de "Total %".
function pctStatClass(pct: number | '--'): string | undefined {
  if (pct === '--') return undefined
  if (pct >= 85) return styles.statSuccess
  if (pct >= 60) return styles.statWarning
  return styles.statDanger
}

interface DetalleSesion {
  fecha: string
  estado: string | null
}

interface Ficha {
  persona: { nombre: string; alias: string | null; foto_url: string | null }
  grupo: string
  fecha_incorporacion: string
  estadisticas: { porcentaje_total: number | '--'; presencias: number; faltas: number; sesiones: number }
  contadores: Record<string, number>
  desglose_mensual: {
    mes: string
    sesiones: number
    presencias: number
    porcentaje: number | '--'
    detalle_sesiones: DetalleSesion[]
  }[]
}

export function FichaOverlayF11({
  equipoId,
  miembroId,
  lang,
  onClose,
  onChanged,
}: {
  equipoId: string
  miembroId: string
  lang: Lang
  onClose: () => void
  onChanged: () => void
}) {
  const api = useAsistenciaApi()
  const [ficha, setFicha] = useState<Ficha | null>(null)
  const [nombre, setNombre] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function refresh() {
    const data: Ficha = await api.get(`/miembros/${miembroId}/ficha?equipo_id=${equipoId}`)
    setFicha(data)
    setNombre(data.persona.nombre)
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miembroId])

  // Issue #80: cambio de rol desde la ficha (misma persona, sin borrarla ni volver a crearla).
  async function handleCambiarRol(grupo: Grupo) {
    await api.patch(`/equipos/${equipoId}/miembros/${miembroId}`, { grupo })
    await refresh()
    onChanged()
  }

  async function handleGuardarNombre() {
    await api.patch(`/equipos/${equipoId}/miembros/${miembroId}`, { nombre })
    await refresh()
    onChanged()
  }

  async function handleSubirFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    await api.postForm(`/miembros/${miembroId}/foto`, formData, 'PATCH')
    await refresh()
    onChanged()
  }

  async function handleEliminarJugador() {
    if (!window.confirm(tBase('eliminarJugadorConfirm', lang))) return
    await api.patch(`/equipos/${equipoId}/miembros/${miembroId}`, {
      fecha_baja: new Date().toISOString().slice(0, 10),
    })
    onChanged()
    onClose()
  }

  if (!ficha) return null

  // Look & feel (2026-10-22): "la leyenda «1 (Asiste): 11, TR (Trabajo): 13» es poco visual… en cada
  // mes salen 2 filas que no se sabe qué son (Agosto 100 y 1 1)". Resumen con los chips de color de la
  // tabla, en el orden de los estados; cada mes con cabecera «N sesiones · %», barra y las
  // sesiones como chips con su día debajo; los meses que aún no han empezado no se muestran.
  const contadoresPositivos = ESTADOS_F11.filter((estado) => (ficha.contadores[estado] ?? 0) > 0).map(
    (estado) => [estado, ficha.contadores[estado]] as const,
  )
  const locale = localeFor(lang)
  const ahora = new Date()
  const mesesVisibles = ficha.desglose_mensual.filter((fila) =>
    fila.detalle_sesiones.some((sesion) => new Date(sesion.fecha) <= ahora),
  )

  return (
    <dialog open onClose={onClose} className={dialogStyles.dialog}>
      <h3>{tBase('fichaJugador', lang)}</h3>

      {ficha.persona.foto_url ? (
        <img className={styles.avatar} src={ficha.persona.foto_url} alt="" width={88} height={88} />
      ) : (
        <span aria-hidden className={styles.avatarFallback}>
          {ficha.persona.nombre.slice(0, 2).toUpperCase()}
        </span>
      )}
      <button onClick={() => fileInputRef.current?.click()}>{tBase('subirFoto', lang)}</button>
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleSubirFoto} />

      <label>
        {tBase('nombre', lang)}
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </label>
      <button onClick={handleGuardarNombre}>{tBase('guardar', lang)}</button>
      <RolSelector grupo={ficha.grupo as Grupo} lang={lang} onChange={handleCambiarRol} />

      <dl className={styles.statsGrid}>
        <dt>{tBase('totalPorc', lang)}</dt>
        <dd className={pctStatClass(ficha.estadisticas.porcentaje_total)}>{ficha.estadisticas.porcentaje_total}</dd>
        <dt>{tBase('presencias', lang)}</dt>
        <dd className={styles.statSuccess}>{ficha.estadisticas.presencias}</dd>
        <dt>{tBase('faltas', lang)}</dt>
        <dd className={styles.statDanger}>{ficha.estadisticas.faltas}</dd>
        <dt>{tBase('sesiones', lang)}</dt>
        <dd>{ficha.estadisticas.sesiones}</dd>
      </dl>

      <h4>{t('resumenTemporada', lang)}</h4>
      <ul className={styles.resumenEstados}>
        {contadoresPositivos.map(([estado, count]) => (
          <li key={estado} className={styles.resumenEstado}>
            <EstadoChip estado={estado} />
            <span className={styles.resumenLabel}>{estadoLabel(estado, lang)}</span>
            <strong className={styles.resumenCount}>{count}</strong>
          </li>
        ))}
      </ul>

      <h4>{t('evolucionMensual', lang)}</h4>
      {mesesVisibles.map((fila) => (
        <section key={fila.mes} className={styles.mes} aria-label={mesLabel(fila.mes, lang)}>
          <div className={styles.mesCabecera}>
            <strong>{mesLabel(fila.mes, lang)}</strong>
            {typeof fila.porcentaje === 'number' ? (
              <span className={styles.mesResumen}>
                {fila.sesiones} {tBase('sesiones', lang).toLowerCase()} ·{' '}
                <span className={pctStatClass(fila.porcentaje)}>{fila.porcentaje} %</span>
              </span>
            ) : (
              <span className={styles.mesResumen}>{t('sinMarcar', lang)}</span>
            )}
          </div>
          {typeof fila.porcentaje === 'number' && (
            <progress className={progressClass(fila.porcentaje)} value={fila.porcentaje} max={100} />
          )}
          <ol className={styles.sesionesMes}>
            {fila.detalle_sesiones.map((sesion, i) => (
              <li key={i} className={styles.sesionDia} title={formatDate(sesion.fecha, locale)}>
                {sesion.estado && (ESTADOS_F11 as readonly string[]).includes(sesion.estado) ? (
                  <EstadoChip estado={sesion.estado as EstadoF11} compact />
                ) : (
                  <span className={styles.sinMarca} aria-label={t('sinMarcar', lang)}>·</span>
                )}
                <span className={styles.dia}>{new Date(sesion.fecha).getUTCDate()}</span>
              </li>
            ))}
          </ol>
        </section>
      ))}

      <div className="modal-actions">
        {/* Issue #76: Excel individual (datos, historial de asistencia y %). */}
        <button onClick={() => guardarFichero(api.downloadFile(`/miembros/${miembroId}/exportar?equipo_id=${equipoId}&lang=${lang}`))}>
          {tBase('excelJugador', lang)}
        </button>
        <button onClick={handleEliminarJugador}>{tBase('eliminarJugador', lang)}</button>
        <button onClick={onClose}>{tBase('cerrar', lang)}</button>
      </div>
    </dialog>
  )
}
